import fetch from 'isomorphic-unfetch';
import merge from 'lodash/merge';
import Router from 'next/router';
import { HEADERS } from '@belong/constants';
import { clearAuthCookie, clearMfaEnrolCookie } from '@belong/cookies';
import { isBlueHost, isBrowser } from '@belong/env';
import { logger } from '@belong/logging/logger';
import type { IGenericFetchResult, IHeaders } from '@belong/types';
import { getRootRelativeUrl, redirectInProgress, urls } from '@belong/url-utils';
import { initAnalytic } from './analyticEvent';
import { DEFAULT_ERROR, getSessionInfo, isUnauthorisedStatus } from './serviceUtils';
import { IAppConfig, IGenericFetch } from './types';

// statuses that are allowed to return an empty payload
const EMPTY_PAYLOAD_STATUSES = [202, 204];

const isHTTPResponseNotOkay = (response: Response): boolean => !response.ok;

const parseResponse = async <T = any>(apiResponse: Response): Promise<T> => {
  try {
    return await apiResponse.json();
  } catch (error) {
    throw new Error(`failed to parse API response as json\n${error}`);
  }
};

const buildRequestHeaders = ({
  headers,
  consumerId,
  correlationId,
  accessToken,
  idToken,
  includeCredentials,
  includeIdToken
}: {
  headers?: IHeaders;
  consumerId: string;
  correlationId: string;
  accessToken: string;
  idToken: string;
  includeCredentials: boolean;
  includeIdToken: boolean;
}): IHeaders =>
  merge(
    {
      [HEADERS.ACCEPT]: 'application/json',
      [HEADERS.CONTENT_TYPE]: 'application/json',
      [HEADERS.CONSUMER_ID]: consumerId,
      [HEADERS.CORRELATION_ID]: correlationId,
      ...(includeCredentials && { [HEADERS.ACCESS_TOKEN]: accessToken }),
      ...(includeIdToken && { [HEADERS.ID_TOKEN]: idToken }),
      // ensure API requests are not cached (similar headers exist in response)
      [HEADERS.CACHE_CONTROL]: 'no-store',
      [HEADERS.PRAGMA]: 'no-cache'
    },
    headers
  );

/**
 * Clears the session and redirects to login for client-initiated 401 responses
 */
const handleUnauthorisedResponse = (
  response: Response,
  { correlationId, accessToken, basePath }: { correlationId: string; accessToken: string; basePath?: string }
): void => {
  const logPrefix = `${getRootRelativeUrl(response.url)} ${correlationId}`;
  logger.error(logPrefix, 'Unauthenticated API response');
  const currentUrl = `${basePath ?? ''}${Router.asPath}`;

  if (redirectInProgress(currentUrl)) {
    return;
  }

  clearAuthCookie();
  clearMfaEnrolCookie();
  const reason = accessToken ? '&reason=session_expired' : '';
  const location = `/login?redirect=${currentUrl}${reason}`;
  // Location change is necessary to ensure a fresh page load / wipe memory AND cookie
  window.location.assign(basePath === 'nbn-agent' ? urls.AGENT_AUTHENTICATION : location);
};

const normaliseErrorBody = (response: Response, responseBody: any): any => {
  if (!responseBody) {
    return DEFAULT_ERROR;
  }

  const isEmptyObject = Object.keys(responseBody).length === 0;

  // handles empty or non-standardised error response body
  if (isEmptyObject || (!responseBody.errorCode && !responseBody.error)) {
    return {
      ...DEFAULT_ERROR,
      status: response.status.toString()
    };
  }

  return responseBody;
};

const resolveResponseBody = async (response: Response): Promise<any> => {
  let responseBody;

  try {
    responseBody = await parseResponse(response);
  } catch {
    if (EMPTY_PAYLOAD_STATUSES.includes(response.status)) {
      logger.debug('Returning empty payload for 204 response');
      responseBody = {};
    } else {
      responseBody = DEFAULT_ERROR;
    }
  }

  return isHTTPResponseNotOkay(response) ? normaliseErrorBody(response, responseBody) : responseBody;
};

/**
 * Fetch from Belong API
 *
 * Adds headers, redirects url, etc.
 */
export const genericFetch = async <T>({
  endpoint,
  headers,
  method = 'GET',
  signal,
  body,
  config,
  includeCredentials = true,
  includeIdToken = false,
  trackedByAnalytics = false,
  ...otherOptions
}: IGenericFetch & IAppConfig): Promise<IGenericFetchResult<T>> => {
  // initialise an analytics service singleton
  const analyticService = initAnalytic();

  const { host, hostBlue, consumerId, basePath } = config;
  const { accessToken = '', correlationId = '', idToken = '' } = getSessionInfo(headers);

  const options: Omit<IGenericFetch, 'endpoint'> = {
    headers: buildRequestHeaders({
      headers,
      consumerId,
      correlationId,
      accessToken,
      idToken,
      includeCredentials,
      includeIdToken
    }),
    method,
    signal,
    body: typeof body !== 'string' ? JSON.stringify(body) : body,
    ...otherOptions
  };

  if (trackedByAnalytics) {
    analyticService.trackAPI(endpoint, 'BEFORE', {});
  }

  const apiHost = isBlueHost() ? hostBlue : host;
  const url = apiHost + endpoint;

  let response;
  try {
    response = await fetch(url, options as any);
    // redirect on client-initiated requests that return 401
    if (isBrowser() && isUnauthorisedStatus(response.status)) {
      handleUnauthorisedResponse(response, { correlationId, accessToken, basePath });

      return {
        status: response.status,
        headers: response.headers,
        body: DEFAULT_ERROR
      };
    }

    if (options.headers?.[HEADERS.ACCEPT] === 'application/pdf') {
      // For PDFs return blob as the body
      return {
        status: response.status,
        headers: response.headers,
        body: await response.blob()
      };
    }

    return {
      status: response.status,
      headers: response.headers,
      body: await resolveResponseBody(response)
    };
  } catch (err) {
    logger.error('API request failed', { url, method, error: err });

    return {
      // 424: Failed Dependency
      status: 424,
      body: {
        status: '424',
        errorCode: DEFAULT_ERROR.errorCode,
        errorDescription: err.message
      }
    };
  } finally {
    if (trackedByAnalytics) {
      analyticService.trackAPI(endpoint, 'AFTER', { ok: Boolean(response?.ok) });
    }
  }
};
